import React, { useState } from 'react';
import { updateSubscription } from '../../services/subscriptionService';

const PlanChangeModal = ({ currentPlan, availablePlans, isOpen, onClose, onSuccess }) => {
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Filter out the current plan from available plans
    const otherPlans = availablePlans.filter(plan => plan.id !== currentPlan.id);

    const handlePlanChange = async () => {
        if (!selectedPlanId) {
            setError('Please select a plan');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const result = await updateSubscription(selectedPlanId);
            onSuccess(result);
            onClose();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to update subscription');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-lg w-full mx-4 shadow-xl">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Change Subscription Plan</h3>
                </div>

                <div className="px-6 py-4">
                    <p className="text-sm text-gray-600 mb-4">
                        You are currently on the <span className="font-semibold">{currentPlan.name}</span> plan.
                        Select a new plan below:
                    </p>

                    {error && (
                        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    <div className="space-y-4 mb-6">
                        {otherPlans.map(plan => (
                            <div
                                key={plan.id}
                                className={`border rounded-md p-4 cursor-pointer transition-colors ${selectedPlanId === plan.id
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-blue-300'
                                    }`}
                                onClick={() => setSelectedPlanId(plan.id)}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="font-medium text-gray-900">{plan.name}</h4>
                                        <p className="text-sm text-gray-600">{plan.description}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-semibold text-blue-600">
                                            ${(plan.price_monthly / 100).toFixed(2)}/month
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {plan.max_users === null ? 'Unlimited users' : `Up to ${plan.max_users} users`}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="bg-gray-50 p-4 rounded-md mb-4">
                        <p className="text-sm text-gray-700">
                            <strong>Note:</strong> Changing plans may result in immediate charges or credits to your account.
                            Your subscription will be updated immediately.
                        </p>
                    </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-4 rounded-b-lg">
                    <button
                        type="button"
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={`px-4 py-2 border border-transparent rounded-md text-white ${loading
                            ? 'bg-blue-400'
                            : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                        onClick={handlePlanChange}
                        disabled={loading || !selectedPlanId}
                    >
                        {loading ? 'Updating...' : 'Change Plan'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PlanChangeModal;